'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  alpha,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Inventory2 as BulkIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForwardIos as ArrowRightIcon,
  AutoAwesome as SparklesIcon,
} from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import useApiCall from '@/services/api/useApiCall';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Bulk Booking Banner for Business Users
 * Compact & intelligent: Shows contract info if active, otherwise promotes bulk booking
 */
export default function BulkBookingBanner() {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { makeApiCall } = useApiCall();
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth?.user);
  const isBusinessUser =
    user?.userType?.toUpperCase() === 'BUSINESS' || user?.user_type === 'BUSINESS';

  // Support multiple auth shapes: userId | id | user_id
  const userId = user?.userId || user?.id || user?.user_id || null;

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch user's contract on mount (or when user becomes available)
  useEffect(() => {
    if (!isBusinessUser || !userId) return;

    const fetchContract = async () => {
      setLoading(true);
      try {
        const config = buildApiConfig(endPointEnums.GET_MY_CONTRACT);
        const res = await makeApiCall(config);
        if (res?.success && res?.data?.status === 'ACTIVE') {
          setContract(res.data);
        }
      } catch (err) {
        console.error('[BulkBookingBanner] Error fetching contract:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
  }, [isBusinessUser, userId, makeApiCall]);

  if (!isBusinessUser) return null;

  if (loading) {
    return (
      <Box sx={{ mb: 2, p: 1.5, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // ========== HAS ACTIVE CONTRACT ==========
  if (contract) {
    return (
      <Box
        sx={{
          mb: 2,
          p: isMobile ? 1.2 : 2,
          px: isMobile ? 1.5 : 2.5,
          borderRadius: isMobile ? 1.5 : 2,
          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          border: '1px solid #6ee7b7',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 1 : 2,
          boxShadow: '0 1px 6px rgba(5, 150, 105, 0.08)',
        }}
      >
        {/* Row 1: Icon + Title + Description */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            flex: 1,
            minWidth: 0,
            width: '100%',
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: isMobile ? 20 : 24,
              color: '#059669',
              flexShrink: 0,
              mt: 0.25,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                color: '#047857',
                fontSize: isMobile ? '0.9rem' : '0.95rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
              }}
              title={contract.contractName}
            >
              {contract.contractName} • {contract.baseDiscountPercent}% off
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#059669',
                display: 'block',
                mt: 0.2,
                fontSize: isMobile ? '0.74rem' : '0.8rem',
              }}
            >
              {t('bulkOrder.discountAllShipments', { percent: contract.baseDiscountPercent })}
            </Typography>
          </Box>
        </Box>

        {/* Row 2 on mobile / inline on desktop: Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 0.75 : 1.2,
            alignItems: 'stretch',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => router.push('/my-pricing')}
            fullWidth={isMobile}
            sx={{
              color: '#059669',
              borderColor: '#059669',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              px: isMobile ? 2 : 1.2,
              py: 0.65,
              border: '1.2px solid',
              flexShrink: 0,
            }}
          >
            {isMobile ? t('bulkOrder.details') : t('bulkOrder.viewDetails')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => router.push('/bulk-booking')}
            endIcon={<ArrowRightIcon />}
            fullWidth={isMobile}
            sx={{
              bgcolor: '#059669',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              px: isMobile ? 2 : 1.5,
              py: 0.65,
              borderRadius: 1.5,
              flexShrink: 0,
              boxShadow: 'none',
              borderRadius: 1,
            }}
          >
            {isMobile ? t('bulkOrder.order') : t('bulkOrder.startOrder')}
          </Button>
        </Box>
      </Box>
    );
  }

  // ========== NO ACTIVE CONTRACT (DEFAULT) ==========
  return (
    <Box
      sx={{
        mb: 2,
        p: isMobile ? 1.2 : 2,
        px: isMobile ? 1.5 : 2.5,
        borderRadius: isMobile ? 1.5 : 2,
        background: 'linear-gradient(135deg, #fbbf24 0%, #fde68a 100%)',
        border: '1px solid #f59e0b',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 1 : 2,
        boxShadow: '0 1px 6px rgba(245, 158, 11, 0.10)',
      }}
    >
      {/* Row 1: Icon + Text */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          minWidth: 0,
          width: '100%',
        }}
      >
        <SparklesIcon sx={{ fontSize: isMobile ? 20 : 24, color: '#d97706', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#b45309',
              fontSize: isMobile ? '0.9rem' : '0.95rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
              lineHeight: 1.3,
            }}
          >
            {t('bulkOrder.saveWithBulk')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#92400e',
              display: 'block',
              mt: 0.2,
              fontSize: isMobile ? '0.74rem' : '0.8rem',
            }}
          >
            {t('bulkOrder.uploadMultiple')}
          </Typography>
        </Box>
      </Box>

      {/* Row 2 on mobile / inline on desktop: CTA button */}
      <Button
        size="small"
        variant="contained"
        onClick={() => router.push('/bulk-booking')}
        endIcon={<ArrowRightIcon />}
        fullWidth={isMobile}
        sx={{
          bgcolor: '#d97706',
          color: '#fff',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: isMobile ? '0.82rem' : '0.85rem',
          px: isMobile ? 2 : 1.6,
          py: 0.65,
          borderRadius: 1.5,
          boxShadow: 'none',
          flexShrink: 0,
          '&:hover': { bgcolor: '#b45309' },
        }}
      >
        {isMobile ? t('bulkOrder.tryBulkBooking') : t('bulkOrder.startBulkBooking')}
      </Button>
    </Box>
  );
}
